import { useState } from 'react';
import { Link } from 'react-router';
import { DemoBadge, Empty, ErrorMsg, Loading, Modal, PageHead, Time } from '../components/ui';
import { api, errorText } from '../lib/api';
import { countryName } from '../lib/format';
import { useApi } from '../lib/session';
import type { MergeSide, MergeSuggestion } from '../lib/types';

function Side({ s, survivor, onPick, name }: { s: MergeSide; survivor: boolean; onPick: () => void; name: string }) {
  return (
    <label className={`merge-side${survivor ? ' survivor' : ''}`}>
      <span className="row gap center">
        <input type="radio" name={name} checked={survivor} onChange={onPick} /> <span className="small">{survivor ? 'Survives' : 'Absorbed'}</span>
      </span>
      <Link to={`/competitors/${s.id}`} className="studio-name">
        {s.name}
      </Link>{' '}
      {s.is_demo && <DemoBadge />}
      <span className="muted small block">{s.domain ?? 'no website'}</span>
      <span className="small block">
        {s.city ?? 'city unknown'}, {countryName(s.country)}
      </span>
      <span className="muted small block">
        added <Time iso={s.created_at} />
      </span>
    </label>
  );
}

function Pair({ p, onDone }: { p: MergeSuggestion; onDone: (msg: string) => void }) {
  // Default survivor: the older record.
  const [survivorId, setSurvivorId] = useState(p.a.created_at <= p.b.created_at ? p.a.id : p.b.id);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const survivor = survivorId === p.a.id ? p.a : p.b;
  const absorbed = survivorId === p.a.id ? p.b : p.a;
  const name = `survivor-${p.a.id}-${p.b.id}`;

  async function merge() {
    setBusy(true);
    setError('');
    try {
      await api('/api/studios/merge', { method: 'POST', body: { source_id: absorbed.id, target_id: survivor.id } });
      onDone(`${absorbed.name} merged into ${survivor.name}.`);
    } catch (e) {
      setError(errorText(e));
      setConfirm(false);
    } finally {
      setBusy(false);
    }
  }
  async function dismiss() {
    setBusy(true);
    setError('');
    try {
      await api('/api/merge-suggestions/dismiss', { method: 'POST', body: { a: p.a.id, b: p.b.id } });
      onDone(`${p.a.name} and ${p.b.name} marked as different studios.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="merge-pair">
      <p className="merge-reason small">
        Possible duplicate: <strong>{p.reason}</strong> <span className="muted">(“{p.key}”)</span>
      </p>
      <div className="merge-sides">
        <Side s={p.a} survivor={survivorId === p.a.id} onPick={() => setSurvivorId(p.a.id)} name={name} />
        <Side s={p.b} survivor={survivorId === p.b.id} onPick={() => setSurvivorId(p.b.id)} name={name} />
      </div>
      <div className="row gap wrap">
        <button type="button" className="btn primary small" disabled={busy} onClick={() => setConfirm(true)}>
          Merge…
        </button>
        <button type="button" className="btn small" disabled={busy} onClick={() => void dismiss()}>
          Not the same studio
        </button>
      </div>
      <ErrorMsg>{error}</ErrorMsg>
      {confirm && (
        <Modal
          title="Merge these records?"
          onClose={() => setConfirm(false)}
          footer={
            <>
              <button type="button" className="btn ghost" onClick={() => setConfirm(false)}>
                Cancel
              </button>
              <button type="button" className="btn primary" disabled={busy} onClick={() => void merge()}>
                {busy ? 'Merging…' : `Merge into ${survivor.name}`}
              </button>
            </>
          }
        >
          <p>
            <strong>{absorbed.name}</strong> will be absorbed into <strong>{survivor.name}</strong>.
          </p>
          <p>
            Nothing is deleted. Domains, sources, facts, notes, social profiles and jobs move to the surviving record, offices are copied, and
            fields empty on the survivor are filled from the absorbed record. Schedules of the absorbed record end. The absorbed record is kept with a
            pointer to the survivor and a snapshot of how it looked, and its old address leads to the survivor.
          </p>
        </Modal>
      )}
    </li>
  );
}

export function Merge() {
  const { data, error, loading, reload } = useApi<{ items: MergeSuggestion[] }>('/api/merge-suggestions');
  const [msg, setMsg] = useState('');
  return (
    <div className="page">
      <p className="crumbs small">
        <Link to="/competitors">Competitors</Link> / Merge suggestions
      </p>
      <PageHead title="Merge suggestions" meta="Possible duplicates" />
      <p className="muted">Pairs with the same normalised name or the same registrable domain. Choose which record survives, or mark them as different.</p>
      {msg && (
        <p className="msg ok" role="status">
          {msg}
        </p>
      )}
      <ErrorMsg>{error}</ErrorMsg>
      {loading && !data ? (
        <Loading what="Looking for duplicates" />
      ) : !data?.items.length ? (
        <Empty>No possible duplicates found.</Empty>
      ) : (
        <ul className="merge-list">
          {data.items.map((p) => (
            <Pair
              key={`${p.a.id}-${p.b.id}`}
              p={p}
              onDone={(m) => {
                setMsg(m);
                reload();
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
